import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { documentService } from '../../services/documentService';
import { DocumentResponse, DocumentCreateRequest, DocumentUpdateRequest, DocumentFilterResponse } from '../../types/api';
import { authService } from '../../services/authService';
import { Edit3, Trash2, Plus, Filter, RefreshCw, Search, LogOut, AlertTriangle, X, FileText, Layers, GraduationCap, BookOpen, Calendar, Upload, Text } from 'lucide-react';

const emptyCreate: DocumentCreateRequest = {
  title: '',
  type: 'PDF',
  category: 'TD',
  level: '',
  subject: '',
  description: '',
  year: ''
};

// Options conformes aux enums backend
const levelOptions = [
  { value: 'SIXIEME', label: '6ème' },
  { value: 'CINQUIEME', label: '5ème' },
  { value: 'QUATRIEME', label: '4ème' },
  { value: 'TROISIEME', label: '3ème' },
  { value: 'SECONDE', label: '2nde' },
  { value: 'PREMIERE', label: '1ère' },
  { value: 'TERMINALE', label: 'Terminale' },
  { value: 'TOUS_NIVEAUX', label: 'Tous niveaux' }
];

const subjectOptions = [
  { value: 'MATHEMATIQUES', label: 'Maths' },
  { value: 'PHYSIQUE_CHIMIE', label: 'PC' },
  { value: 'SVT', label: 'SVT' },
  { value: 'INFORMATIQUE', label: 'Informatique' },
  { value: 'FRANCAIS', label: 'Français' },
  { value: 'ANGLAIS', label: 'Anglais' },
  { value: 'HISTOIRE_GEOGRAPHIE', label: 'HG' },
  { value: 'PHILOSOPHIE', label: 'Philo' }
];

const isValidSchoolYear = (value: string): boolean => {
  if (!/^\d{4}-\d{4}$/.test(value)) return false;
  const [startStr, endStr] = value.split('-');
  const start = parseInt(startStr, 10);
  const end = parseInt(endStr, 10);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
  return end === start + 1;
};

const isValidCalendarYear = (value: string): boolean => {
  return /^\d{4}$/.test(value);
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [docs, setDocs] = useState<DocumentResponse[]>([]);
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<DocumentCreateRequest>(emptyCreate);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [filters, setFilters] = useState<DocumentFilterResponse>({ levels: [], subjects: [] });
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteTitle, setDeleteTitle] = useState<string>('');
  const [yearError, setYearError] = useState<string | null>(null);

  // Filtres de requête
  const [filterCategory, setFilterCategory] = useState<'' | 'OFFICIAL' | 'TD' | 'CORRECTION' | 'EXAMEN' | 'DEVOIR'>('');
  const [filterLevel, setFilterLevel] = useState<string>('');
  const [filterSubject, setFilterSubject] = useState<string>('');
  const [filterType, setFilterType] = useState<'' | 'PDF' | 'DOCX' | 'XLSX'>('');
  const [filterYear, setFilterYear] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const stats = useMemo(() => {
    const totalDocuments = docs.length;
    const years = new Set(docs.map(d => String(d.year ?? '')).filter(Boolean));
    const subjects = new Set(docs.map(d => d.subject).filter(Boolean));
    return { totalDocuments, totalYears: years.size, totalSubjects: subjects.size };
  }, [docs]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      // Prépare l’année: accepte YYYY ou YYYY-YYYY, on envoie tel quel si valide
      let yearParam: string | undefined;
      if (filterYear) {
        const val = filterYear.trim();
        yearParam = (isValidCalendarYear(val) || isValidSchoolYear(val)) ? val : undefined;
      }
      const res = await documentService.getAllDocuments(
        page,
        size,
        filterCategory || undefined,
        filterLevel || undefined,
        filterSubject || undefined,
        filterType || undefined,
        searchTerm || undefined,
        yearParam
      );
      setDocs(res.content);
      setTotalPages(res.totalPages);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadFilters = async () => {
    try {
      const f = await documentService.getFilters();
      setFilters(f);
    } catch (_e) {
      // silencieux
    }
  };

  useEffect(() => { load(); }, [page, filterCategory, filterLevel, filterSubject, filterType, filterYear, searchTerm]);
  useEffect(() => { loadFilters(); }, []);

  const startEdit = (doc: DocumentResponse) => {
    setEditingId(doc.id);
    // Déterminer l'enum à partir de la valeur retournée (enum name ou label)
    const levelEnum = levelOptions.find(o => o.value === doc.level) || levelOptions.find(o => o.label === doc.level);
    const subjectEnum = subjectOptions.find(o => o.value === doc.subject) || subjectOptions.find(o => o.label === doc.subject);
    setForm({
      title: doc.title,
      type: doc.type as any,
      category: doc.category as any,
      level: levelEnum ? levelEnum.value : '',
      subject: subjectEnum ? subjectEnum.value : '',
      description: doc.description,
      year: doc.year ? String(doc.year) : ''
    });
    setFile(undefined);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validation année scolaire
    if (!isValidSchoolYear(form.year || '')) {
      setYearError('Veuillez saisir une année scolaire valide (ex: 2024-2025).');
      return;
    }
    try {
      setLoading(true);
      setSuccess(null);
      const payload: DocumentCreateRequest = { ...form, file };
      await documentService.createDocument(payload);
      setCreating(false);
      setForm(emptyCreate);
      setFile(undefined);
      setYearError(null);
      setSuccess('Document créé avec succès');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Erreur de création');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    if (!isValidSchoolYear(form.year || '')) {
      setYearError('Veuillez saisir une année scolaire valide (ex: 2024-2025).');
      return;
    }
    try {
      setLoading(true);
      setSuccess(null);
      const payload: DocumentUpdateRequest = { ...form, file } as any;
      await documentService.updateDocument(editingId, payload);
      setEditingId(null);
      setForm(emptyCreate);
      setFile(undefined);
      setYearError(null);
      setSuccess('Document mis à jour');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Erreur de mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const openDelete = (doc: DocumentResponse) => {
    setDeleteId(doc.id);
    setDeleteTitle(doc.title);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      setLoading(true);
      setSuccess(null);
      await documentService.deleteDocument(deleteId);
      setShowDelete(false);
      setDeleteId(null);
      setDeleteTitle('');
      setSuccess('Document supprimé');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Erreur de suppression');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/admin/login', { replace: true });
  };

  const resetFilters = () => {
    setFilterCategory('');
    setFilterLevel('');
    setFilterSubject('');
    setFilterType('');
    setFilterYear('');
    setSearchTerm('');
    setPage(0);
  };

  const availableYears = useMemo(() => {
    if (filters.years && filters.years.length) return filters.years;
    const ys = Array.from(new Set(docs.map(d => String(d.year ?? '')).filter(Boolean)));
    return ys.sort((a, b) => b.localeCompare(a));
  }, [filters.years, docs]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src="/images/logo.png?v=1" alt="LES CRAC ACADEMY" className="w-10 h-10 object-contain" />
            <div>
              <h1 className="text-2xl font-extrabold text-gray-900 leading-tight">CRAC Console</h1>
              <p className="text-xs text-gray-500">Administration — Gestion des documents</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={() => { setEditingId(null); setForm(emptyCreate); setFile(undefined); setCreating(true); }} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow inline-flex items-center space-x-2"><Plus className="h-4 w-4" /><span>Nouveau</span></button>
            <button onClick={handleLogout} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 inline-flex items-center space-x-2"><LogOut className="h-4 w-4" /><span>Se déconnecter</span></button>
          </div>
        </div>

        {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</div>}
        {success && <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">{success}</div>}

        {/* Filtres */}
        <div className="mb-6 bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-gray-700 inline-flex items-center space-x-2"><Filter className="h-4 w-4" /><span>Filtres</span></div>
            <div className="space-x-2">
              <button onClick={() => { setPage(0); load(); }} className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 inline-flex items-center space-x-2"><RefreshCw className="h-4 w-4" /><span>Rafraîchir</span></button>
              <button onClick={resetFilters} className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100">Réinitialiser</button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <select className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500" value={filterCategory} onChange={e => { setFilterCategory(e.target.value as any); setPage(0); }}>
              <option value="">Catégorie</option>
              <option value="OFFICIAL">OFFICIAL</option>
              <option value="TD">TD</option>
              <option value="CORRECTION">CORRECTION</option>
              <option value="EXAMEN">EXAMEN</option>
              <option value="DEVOIR">DEVOIR</option>
            </select>
            <select className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500" value={filterType} onChange={e => { setFilterType(e.target.value as any); setPage(0); }}>
              <option value="">Type</option>
              <option value="PDF">PDF</option>
              <option value="DOCX">DOCX</option>
              <option value="XLSX">XLSX</option>
            </select>
            <select className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500" value={filterLevel} onChange={e => { setFilterLevel(e.target.value); setPage(0); }}>
              <option value="">Niveau</option>
              {filters.levels?.map(l => (<option key={l} value={l}>{l}</option>))}
            </select>
            <select className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500" value={filterSubject} onChange={e => { setFilterSubject(e.target.value); setPage(0); }}>
              <option value="">Matière</option>
              {filters.subjects?.map(s => (<option key={s} value={s}>{s}</option>))}
            </select>
            <select className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500" value={filterYear} onChange={e => { setFilterYear(e.target.value.trim()); setPage(0); }}>
              <option value="">Année</option>
              {availableYears.map(y => (<option key={y} value={y}>{y}</option>))}
            </select>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setPage(0); }} placeholder="Rechercher..." className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2.5 focus:border-blue-500 focus:ring-blue-500" />
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="rounded-2xl p-5 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 shadow-sm">
            <div className="text-sm text-blue-700">Documents</div>
            <div className="mt-1 text-3xl font-extrabold text-blue-900">{stats.totalDocuments}</div>
          </div>
          <div className="rounded-2xl p-5 bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-100 shadow-sm">
            <div className="text-sm text-emerald-700">Années</div>
            <div className="mt-1 text-3xl font-extrabold text-emerald-900">{stats.totalYears}</div>
          </div>
          <div className="rounded-2xl p-5 bg-gradient-to-br from-purple-50 to-fuchsia-50 border border-purple-100 shadow-sm">
            <div className="text-sm text-purple-700">Matières</div>
            <div className="mt-1 text-3xl font-extrabold text-purple-900">{stats.totalSubjects}</div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 text-gray-700 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left">Titre</th>
                  <th className="px-4 py-3 text-left">Niveau</th>
                  <th className="px-4 py-3 text-left">Matière</th>
                  <th className="px-4 py-3 text-left">Catégorie</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Année</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="text-sm divide-y divide-gray-100">
                {docs.map((d) => (
                  <tr key={d.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{d.title}</td>
                    <td className="px-4 py-3 text-gray-700">{d.level}</td>
                    <td className="px-4 py-3 text-gray-700">{d.subject}</td>
                    <td className="px-4 py-3 text-gray-700">{d.category}</td>
                    <td className="px-4 py-3 text-gray-700">{d.type}</td>
                    <td className="px-4 py-3 text-gray-700">{d.year ?? '-'}</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button onClick={() => startEdit(d)} className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-100 inline-flex items-center space-x-2"><Edit3 className="h-4 w-4" /><span>Éditer</span></button>
                      <button onClick={() => openDelete(d)} className="px-3 py-1.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 inline-flex items-center space-x-2"><Trash2 className="h-4 w-4" /><span>Supprimer</span></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="p-3 flex items-center justify-between text-sm text-gray-600">
            <div>Page {page + 1} / {Math.max(totalPages, 1)}</div>
            <div className="space-x-2">
              <button disabled={page===0} onClick={() => setPage(p => Math.max(0, p-1))} className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-50">Précédent</button>
              <button disabled={page+1>=totalPages} onClick={() => setPage(p => p+1)} className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-50">Suivant</button>
            </div>
          </div>
        </div>

        {/* Modal Create / Edit */}
        {(creating || editingId) && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
            <div className="w-full max-w-xl bg-white rounded-2xl p-6 shadow-xl border border-gray-200">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold">{editingId ? 'Modifier' : 'Nouveau'} document</h2>
                  <p className="text-xs text-gray-500">Renseignez les informations du document à {editingId ? 'mettre à jour' : 'ajouter'}.</p>
                </div>
                <button onClick={() => { setCreating(false); setEditingId(null); setForm(emptyCreate); setFile(undefined); }} className="text-gray-500 hover:text-gray-700"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={editingId ? handleUpdate : handleCreate} className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titre <span className="text-red-600">*</span></label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <FileText className="h-4 w-4 text-gray-400" />
                    </div>
                    <input className="w-full rounded-lg border-gray-300 pl-9 focus:border-blue-500 focus:ring-blue-500" placeholder="Ex: Suite d'exercices trigonométrie" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type <span className="text-red-600">*</span></label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <Layers className="h-4 w-4 text-gray-400" />
                      </div>
                      <select className="w-full rounded-lg border-gray-300 pl-9 focus:border-blue-500 focus:ring-blue-500" value={form.type} onChange={e => setForm({ ...form, type: e.target.value as any })} required>
                        <option value="PDF">PDF</option>
                        <option value="DOCX">DOCX</option>
                        <option value="XLSX">XLSX</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie <span className="text-red-600">*</span></label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <Layers className="h-4 w-4 text-gray-400" />
                      </div>
                      <select className="w-full rounded-lg border-gray-300 pl-9 focus:border-blue-500 focus:ring-blue-500" value={form.category} onChange={e => setForm({ ...form, category: e.target.value as any })} required>
                        <option value="OFFICIAL">OFFICIAL</option>
                        <option value="TD">TD</option>
                        <option value="CORRECTION">CORRECTION</option>
                        <option value="EXAMEN">EXAMEN</option>
                        <option value="DEVOIR">DEVOIR</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Niveau <span className="text-red-600">*</span></label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <GraduationCap className="h-4 w-4 text-gray-400" />
                      </div>
                      <select className="w-full rounded-lg border-gray-300 pl-9 focus:border-blue-500 focus:ring-blue-500" value={form.level || ''} onChange={e => setForm({ ...form, level: e.target.value })} required>
                        <option value="">Sélectionner…</option>
                        {levelOptions.map(l => (<option key={l.value} value={l.value}>{l.label}</option>))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Matière <span className="text-red-600">*</span></label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <BookOpen className="h-4 w-4 text-gray-400" />
                      </div>
                      <select className="w-full rounded-lg border-gray-300 pl-9 focus:border-blue-500 focus:ring-blue-500" value={form.subject || ''} onChange={e => setForm({ ...form, subject: e.target.value })} required>
                        <option value="">Sélectionner…</option>
                        {subjectOptions.map(s => (<option key={s.value} value={s.value}>{s.label}</option>))}
                      </select>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Année <span className="text-red-600">*</span></label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Calendar className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      className={`w-full rounded-lg border pl-9 focus:border-blue-500 focus:ring-blue-500 ${yearError ? 'border-red-400' : 'border-gray-300'}`}
                      placeholder="Ex: 2024-2025"
                      value={form.year || ''}
                      onChange={e => {
                        const val = e.target.value;
                        setForm({ ...form, year: val });
                        setYearError(val ? (isValidSchoolYear(val) ? null : 'Format attendu: 2024-2025 (années consécutives)') : 'Ce champ est requis');
                      }}
                      required
                    />
                  </div>
                  {yearError && <p className="mt-1 text-xs text-red-600">{yearError}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <div className="relative">
                    <div className="pointer-events-none absolute top-3 left-0 flex items-start pl-3">
                      <Text className="h-4 w-4 text-gray-400" />
                    </div>
                    <textarea className="w-full rounded-lg border-gray-300 pl-9 focus:border-blue-500 focus:ring-blue-500" placeholder="Quelques détails…" value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fichier <span className="text-red-600">*</span></label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Upload className="h-4 w-4 text-gray-400" />
                    </div>
                    <input type="file" onChange={e => setFile(e.target.files?.[0])} className="block w-full text-sm text-gray-700 pl-9" required={editingId === null} />
                  </div>
                </div>
                <div className="flex items-center justify-end space-x-3 mt-2">
                  <button type="button" onClick={() => { setCreating(false); setEditingId(null); }} className="px-4 py-2 rounded-lg border border-gray-300">Annuler</button>
                  <button type="submit" disabled={loading} className="px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-60">{editingId ? 'Enregistrer' : 'Créer'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Confirmation Suppression */}
        {showDelete && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
            <div className="w-full max-w-md bg-white rounded-2xl p-6 shadow-xl border border-gray-200">
              <div className="flex items-start space-x-3">
                <div className="shrink-0"><AlertTriangle className="h-6 w-6 text-red-600" /></div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Confirmer la suppression</h3>
                  <p className="text-sm text-gray-600 mt-1">Voulez-vous vraiment supprimer « {deleteTitle || 'ce document'} » ? Cette action est irréversible.</p>
                </div>
              </div>
              <div className="mt-5 flex items-center justify-end space-x-3">
                <button onClick={() => { setShowDelete(false); setDeleteId(null); setDeleteTitle(''); }} className="px-4 py-2 rounded-lg border border-gray-300">Annuler</button>
                <button onClick={confirmDelete} disabled={loading} className="px-4 py-2 rounded-lg bg-red-600 text-white disabled:opacity-60">Supprimer</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
